import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Sigma, Atom, Globe2, BrainCircuit, Landmark, Palette,
  Telescope, Dna, Music4, Languages, Cpu, TrendingUp,
} from "lucide-react";

const DOMAINS = [
  { key: "Mathématiques", icon: Sigma },
  { key: "Sciences", icon: Atom },
  { key: "Géographie", icon: Globe2 },
  { key: "Philosophie", icon: BrainCircuit },
  { key: "Histoire", icon: Landmark },
  { key: "Art", icon: Palette },
  { key: "Astronomie", icon: Telescope },
  { key: "Biologie", icon: Dna },
  { key: "Musique", icon: Music4 },
  { key: "Langues", icon: Languages },
  { key: "Technologie", icon: Cpu },
  { key: "Économie", icon: TrendingUp },
];

export default function Domaines({ onExplore }) {
  const { t } = useTranslation();
  return (
    <div className="h-full w-full overflow-y-auto scroll-thin relative">
      {/* warm centered glow */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(60% 55% at 50% 45%, hsl(30 85% 55% / 0.22), transparent 70%)",
        }}
      />
      <div className="relative max-w-5xl mx-auto px-6 py-12 sm:py-16 text-center">
        <p className="text-primary/80 tracking-[0.25em] uppercase text-xs mb-4">{t("domaines.eyebrow")}</p>
        <h2 className="font-display text-gradient-orange text-4xl sm:text-5xl italic mb-3">
          {t("domaines.titleA")}<span className="text-primary">{t("domaines.titleHi")}</span>{t("domaines.titleB")}
        </h2>
        <p className="text-foreground/50 max-w-lg mx-auto mb-14 leading-relaxed">
          {t("domaines.subtitle")}
        </p>

        <div className="flex flex-wrap items-center justify-center gap-8 md:gap-10">
          {DOMAINS.map((d, i) => {
            const Icon = d.icon;
            const label = t(`domaines.names.${d.key}`);
            const prompt = t("domaines.prompt", { d: label });
            return (
              <motion.button
                key={d.key}
                data-testid={`domain-${d.key}`}
                onClick={() => onExplore(prompt)}
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.95 }}
                style={{ animationDelay: `${(i % 4) * 0.6}s` }}
                className="group relative w-28 h-28 md:w-32 md:h-32 flex-shrink-0 flex items-center justify-center frame-pulse rounded-full"
              >
                {/* halo pulsé */}
                <motion.span
                  aria-hidden
                  className="absolute rounded-full"
                  style={{
                    inset: "-22%",
                    background:
                      "radial-gradient(circle, hsl(32 90% 60% / 0.55), transparent 68%)",
                    filter: "blur(10px)",
                  }}
                  animate={{ opacity: [0.35, 0.85, 0.35], scale: [0.92, 1.12, 0.92] }}
                  transition={{ duration: 3.2 + (i % 3) * 0.6, repeat: Infinity, ease: "easeInOut" }}
                />
                {/* bulle légèrement fondue */}
                <span
                  className="relative w-full h-full rounded-full flex flex-col items-center justify-center"
                  style={{
                    background:
                      "radial-gradient(circle at 36% 30%, hsl(40 96% 74%), hsl(30 88% 55%) 58%, hsl(22 82% 45%) 100%)",
                    boxShadow:
                      "inset 0 -10px 26px hsl(20 70% 30% / 0.45), inset 0 8px 18px hsl(45 100% 88% / 0.45)",
                    WebkitMaskImage:
                      "radial-gradient(circle, #000 62%, rgba(0,0,0,0.15) 100%)",
                    maskImage:
                      "radial-gradient(circle, #000 62%, rgba(0,0,0,0.15) 100%)",
                  }}
                >
                  <Icon className="text-[hsl(22,60%,20%)] mb-1" size={26} strokeWidth={1.6} />
                  <span className="font-display font-semibold text-[hsl(22,55%,16%)] leading-tight px-2 text-sm">
                    {label}
                  </span>
                </span>
                <span className="absolute inset-0 rounded-full ring-0 group-hover:ring-2 ring-white/40 transition-all" />
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
